import { useState, useCallback, useEffect, useSyncExternalStore } from 'react';
import { getPendingCount } from '../lib/offline/offlineDb';
import { isNetworkOnline, subscribeConnectivity } from '../lib/connectivity';

function subscribe(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  const unsubscribe = subscribeConnectivity(callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
    unsubscribe();
  };
}

function getSnapshot(): boolean {
  // navigator.onLine combined with real request outcomes — the browser flag
  // alone misses "claims online but requests fail" (VIVATISPDB-435).
  return isNetworkOnline();
}

export function useOfflineStatus() {
  const isOnline = useSyncExternalStore(subscribe, getSnapshot);
  const [pendingCount, setPendingCount] = useState(0);

  const refreshPendingCount = useCallback(async () => {
    const count = await getPendingCount();
    setPendingCount(count);
  }, []);

  // Auto-refresh pending count when sync queue changes
  useEffect(() => {
    const handler = () => { refreshPendingCount(); };
    window.addEventListener('sync-queue-changed', handler);
    // Initial count on mount
    getPendingCount().then(setPendingCount);
    return () => window.removeEventListener('sync-queue-changed', handler);
  }, [refreshPendingCount]);

  return { isOnline, pendingCount, refreshPendingCount };
}
